import { useCallback, useMemo } from 'react';
import { useRxData } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import { isJestTest } from '../../test-helpers/isJestTest';
import { InspectionStatus, TaskStatus } from '../../utilities/enums';
import { isTaskAssignedtoFieldInspector } from '../../utilities/utilityFunctions';
import { InspectionList, InspectionListCollection } from '../inspectionList/queryBuilder';
import { RxdbCollectionName } from '../rxdbCollectionName';
import { TaskList, TaskListCollection } from './queryBuilder';

const useAccessOnTask = () => {
  const {
    CurrentUserSelected: { workPackageId, inspectionId, taskId, currentUser, isAdhoc },
  } = useMst();

  const getTaskQuery = useCallback(
    (collection: TaskListCollection) =>
      collection?.find({
        selector: {
          pkAssetInspectionTask: taskId,
          fkSchedule: workPackageId,
          isActive: { $eq: true },
        },
      }),
    [taskId, workPackageId],
  );
  const getInspectionQuery = useCallback(
    (collection: InspectionListCollection) =>
      collection?.find({
        selector: { inspectionId: String(inspectionId), workPackageId },
      }),
    [inspectionId, workPackageId],
  );

  const { result: tasks } = useRxData<TaskList>(RxdbCollectionName.TASK_LIST, getTaskQuery);
  const { result: inspections } = useRxData<InspectionList>(
    RxdbCollectionName.INSPECTION_LIST,
    getInspectionQuery,
  );

  const isLiFiSame = useMemo(() => {
    const task = tasks[0];
    return task?.fkInspectorField === task?.fkInspectorLead;
  }, [tasks]);

  const isLiFiScSame = useMemo(() => {
    const task = tasks[0];
    return (
      task?.fkInspectorField === task?.fkInspectorLead &&
      task?.fieldInspectorUserId === task?.siteCoordinatorUserId
    );
  }, [tasks]);

  const isEditable = useMemo(() => {
    if (isJestTest()) return isTaskAssignedtoFieldInspector();
    const task = tasks[0];
    const inspection = inspections[0];
    const taskStatusList = isAdhoc
      ? [TaskStatus.ASSIGNED, TaskStatus.IN_PROGRESS]
      : [TaskStatus.ASSIGNED, TaskStatus.IN_PROGRESS, TaskStatus.FI_SIGN_PENDING];
    const inspectionStatusList = isAdhoc
      ? [InspectionStatus.ASSIGNED, InspectionStatus.IN_PROGRESS]
      : [InspectionStatus.ASSIGNED, InspectionStatus.IN_PROGRESS, InspectionStatus.FI_SIGN_PENDING];
    return (
      (task?.clerkId &&
        task?.clerkId !== currentUser?.clerkId &&
        task?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL) ||
      (((task?.fkInspectorField && task?.fkInspectorField === currentUser?.inspectorId) ||
        (task?.clerkId && task?.clerkId === currentUser?.clerkId)) &&
        ((taskStatusList.includes(task?.fkInspectionTaskStatusCategory) &&
          inspectionStatusList.includes(inspection?.assetInspectionStatusId)) ||
          (!isAdhoc &&
            isLiFiSame &&
            task?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING &&
            inspection?.assetInspectionStatusId === InspectionStatus.LI_SIGN_PENDING)))
    );
  }, [tasks, inspections, isAdhoc, isLiFiSame]);

  return { isEditable, taskData: tasks[0], inspection: inspections[0], isLiFiSame, isLiFiScSame };
};

export default useAccessOnTask;
